import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ActivityMetrics {
  dau: number;
  wau: number;
  mau: number;
  stickiness: number;
  daily: { day: string; active_users: number }[];
  frequency: Record<"1" | "2-5" | "6-15" | "16+", number>;
  retention: {
    new_signups: number;
    returning: number;
    never_returned: number;
    rate: number;
  };
  churned: number;
}

export interface TopActiveUser {
  user_id: string;
  full_name: string | null;
  email: string | null;
  plan_type: string;
  days_active: number;
  total_hits: number;
  last_activity_at: string | null;
}

export interface DormantUser {
  user_id: string;
  full_name: string | null;
  email: string | null;
  plan_type: string;
  created_at: string;
  last_activity_at: string | null;
  days_inactive: number;
}

export function useUserActivityMetrics(from: string, to: string) {
  return useQuery({
    queryKey: ["admin-user-activity-metrics", from, to],
    queryFn: async (): Promise<ActivityMetrics> => {
      const { data, error } = await supabase.rpc("admin_user_activity_metrics", {
        p_from: from,
        p_to: to,
      });
      if (error) throw error;
      return data as unknown as ActivityMetrics;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useTopActiveUsers(from: string, to: string) {
  return useQuery({
    queryKey: ["admin-top-active-users", from, to],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_top_active_users", {
        p_from: from,
        p_to: to,
        p_limit: 20,
      });
      if (error) throw error;
      return (data ?? []) as TopActiveUser[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useDormantUsers(days: number) {
  return useQuery({
    queryKey: ["admin-dormant-users", days],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_dormant_users", {
        p_days: days,
      });
      if (error) throw error;
      return (data ?? []) as DormantUser[];
    },
    staleTime: 5 * 60 * 1000,
  });
}
